import { useQuery } from '@tanstack/react-query'
import type { CSSProperties } from 'react'
import { useLayoutEffect, useState } from 'react'
import { channelKindToken, conversationQuery } from '@/entities/conversation'
import { Icon, Popover, Skeleton } from '@/shared/ui'
import type { MessageSend } from '../model/use-message-send'
import s from './MessageComposer.module.css'

type MessageComposerProps = {
  conversationId: string
  /** Композер и очередь отправки — один хук на весь тред, см. `useMessageSend`. */
  send: MessageSend
}

/**
 * Композер под лентой (обновлённый макет чата). Главное новое — **выбор канала отправки**: лента
 * брони мультиканальна, и менеджер выбирает, каким родом слать (мессенджер / бот / SMS / веб).
 * Пилюля канала теперь настоящий селектор (`Popover`) со списком `ConversationDetail.sendChannels`;
 * неподключённые каналы показаны, но выбрать их нельзя («не подключён»). Выбранный род уходит в
 * `send.send(kind)` → тело `sendMessage.channelKind`.
 *
 * Поле ввода: рамка на ОБЁРТКЕ (поэтому `Textarea` кита не подошёл), высота растёт под текст до
 * 110px (JS ниже), Enter отправляет, Shift+Enter — перенос.
 */
export function MessageComposer({ conversationId, send }: MessageComposerProps) {
  const { data: conversation, error } = useQuery(conversationQuery(conversationId))
  const { text, setText, inputRef } = send

  const [selectedKind, setSelectedKind] = useState<string | null>(null)
  const [menuOpen, setMenuOpen] = useState(false)

  /*
   * Поле растёт под текст: сбрасываем высоту, потом ставим по `scrollHeight` — иначе поле умеет
   * только расти и не сжимается, когда текст стёрли. `useLayoutEffect` — до покраски.
   */
  useLayoutEffect(() => {
    const input = inputRef.current
    if (!input) return
    input.style.height = 'auto'
    input.style.height = `${input.scrollHeight}px`
  }, [text, inputRef])

  const sendChannels = conversation?.sendChannels ?? []
  const available = sendChannels.filter((channel) => channel.available)
  // Выбранный канал — тот, что отметил менеджер (если он ещё доступен), иначе первый доступный.
  const active = available.find((channel) => channel.kind === selectedKind) ?? available[0] ?? null

  /*
   * Плейсхолдер — из макета: «Сообщение через <канал>…», а когда висит черновик ИИ — «Или напишите
   * свой ответ…». Пока шапка не загрузилась, канал неизвестен — плейсхолдера нет вовсе (пустой
   * молчит, выдуманный утверждает).
   */
  const placeholder = !conversation
    ? undefined
    : conversation.aiDraft
      ? 'Или напишите свой ответ…'
      : active
        ? `Сообщение через ${active.label}…`
        : 'Сообщение…'

  return (
    <div className={s.root}>
      {conversation ? (
        <Popover
          open={menuOpen}
          onOpenChange={setMenuOpen}
          align="start"
          trigger={
            <button
              type="button"
              className={s.pill}
              style={{ '--msg': channelKindToken(active?.kind ?? 'messenger') } as CSSProperties}
              aria-label="Канал отправки"
            >
              <span className={s.pillDot} aria-hidden="true" />
              {active?.label ?? 'Канал'}
              <Icon name="chevron-down" size={11} strokeWidth={2.2} className={s.pillChevron} />
            </button>
          }
        >
          <div className={s.menu}>
            <p className={s.menuTitle}>Отправить через</p>
            {sendChannels.map((channel) => (
              <button
                key={channel.kind}
                type="button"
                className={s.menuItem}
                aria-pressed={channel.kind === active?.kind}
                disabled={!channel.available}
                onClick={() => {
                  setSelectedKind(channel.kind)
                  setMenuOpen(false)
                  inputRef.current?.focus()
                }}
              >
                <span
                  className={s.menuDot}
                  style={{ '--msg': channelKindToken(channel.kind) } as CSSProperties}
                  aria-hidden="true"
                />
                <span className={s.menuLabel}>{channel.label}</span>
                {channel.available ? (
                  channel.kind === active?.kind ? (
                    <Icon name="check" size={13} strokeWidth={2.4} className={s.menuCheck} />
                  ) : null
                ) : (
                  <span className={s.menuUnavail}>не подключён</span>
                )}
              </button>
            ))}
          </div>
        </Popover>
      ) : error ? null : (
        <Skeleton width={104} height={26} radius={999} />
      )}

      <div className={s.field}>
        <textarea
          ref={inputRef}
          className={s.input}
          rows={1}
          value={text}
          placeholder={placeholder}
          aria-label="Сообщение гостю"
          onChange={(event) => setText(event.target.value)}
          onKeyDown={(event) => {
            /*
             * Enter отправляет, Shift+Enter — перенос. `isComposing` — сверх макета: при наборе
             * через IME Enter ПОДТВЕРЖДАЕТ слово, а не заканчивает мысль; без проверки такое
             * подтверждение отправило бы гостю полуслово.
             */
            if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
              event.preventDefault()
              send.send(active?.kind)
            }
          }}
        />

        <button
          type="button"
          className={s.sendButton}
          title="Отправить сообщение"
          aria-label="Отправить сообщение"
          onClick={() => send.send(active?.kind)}
        >
          <Icon name="arrow-right" size={16} strokeWidth={2} />
        </button>
      </div>
    </div>
  )
}
